import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../api/portalApi';
import { useRuntimeConfig } from './RuntimeConfigContext';
import type { Project } from '../types';

const STORAGE_KEY = 'fint_active_project_id';
const LAST_REAL_STORAGE_KEY = 'fint_last_real_project_id';
const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

// Historical sentinel for the dropdown/filter's now-removed "Unassigned" option — kept
// only because the backend's ListSessions ?projectId= sentinel
// (ReviewProcessor.UnassignedProjectSentinel) and AdminProcessor's PatchProjectSelection
// still recognize it as dead-but-harmless code (see
// .claude/rules/15-project-git-config-mandatory-selection.md). No UI in this app sends it
// anymore — project selection is mandatory, so `activeProjectId` is always either a real
// project id or `null` ("not yet selected", a blocking state — see RequireProject.tsx).
export const UNASSIGNED_PROJECT_FILTER = 'none';

function isRealProjectId(id: string | null): id is string {
  return !!id && id !== UNASSIGNED_PROJECT_FILTER;
}

export interface ProjectCtx {
  projects: Project[];
  /** Include-archived lookup, used only to resolve display names — never for selection. */
  archivedProjects: Project[];
  archivedLoading: boolean;
  /** A real project id, or `null` while nothing has been selected yet (blocking state). */
  activeProjectId: string | null;
  activeProject: Project | null;
  /** Last *real* project selected — used to tag new sessions. Distinct from activeProjectId
   *  mainly for historical/backend-contract reasons now that both are effectively always a
   *  real id in steady state; see .claude/rules/15-project-git-config-mandatory-selection.md. */
  lastRealProjectId: string | null;
  loading: boolean;
  setActiveProjectId: (id: string | null) => void;
  clearLastRealProjectId: () => void;
  refreshProjects: () => Promise<void>;
}

const ProjectContext = createContext<ProjectCtx>({
  projects: [],
  archivedProjects: [],
  archivedLoading: true,
  activeProjectId: null,
  activeProject: null,
  lastRealProjectId: null,
  loading: true,
  setActiveProjectId: () => {},
  clearLastRealProjectId: () => {},
  refreshProjects: async () => {},
});

export function useProject() {
  return useContext(ProjectContext);
}

interface Props {
  children: React.ReactNode;
}

// Tracks the org's Projects (product/project context profiles), which one is "active"
// for AI generation/dashboard filtering, and which one was last *really* selected (for
// tagging new sessions) — mirrors UserRoleContext's provider pattern, but does its own
// data fetching (no gate states needed; an empty/failed list just means the app runs in
// fallback/no-context mode, per the Context Composer's design).
export function ProjectProvider({ children }: Props) {
  const { instance } = useMsal();
  const cfg = useRuntimeConfig();
  const [projects, setProjects] = useState<Project[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [archivedLoading, setArchivedLoading] = useState(true);
  const [activeProjectId, setActiveProjectIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY),
  );
  const [lastRealProjectId, setLastRealProjectIdState] = useState<string | null>(
    () => localStorage.getItem(LAST_REAL_STORAGE_KEY),
  );

  const refreshProjects = useCallback(async () => {
    if (DEV_BYPASS) { setLoading(false); setArchivedLoading(false); return; }
    const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
    try {
      setProjects(await api.getProjects());
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
    // Archived-inclusive lookup, kept only for resolving display names of sessions
    // tagged to a since-archived project — never fed into the dropdown/picker.
    try {
      setArchivedProjects(await api.getProjects(true));
    } catch {
      setArchivedProjects([]);
    } finally {
      setArchivedLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, cfg.msalClientId, cfg.apiBaseUrl]);

  useEffect(() => { refreshProjects(); }, [refreshProjects]);

  // Backend (UserAccess row, same one GetMe reads) is the source of truth shared with the
  // Chrome extension — reconcile the localStorage-seeded value against it once per mount.
  // A failed fetch (offline/401) just keeps whatever localStorage already had, matching
  // refreshProjects' own graceful-degradation style.
  useEffect(() => {
    if (DEV_BYPASS) return;
    const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
    api.getProjectSelection()
      .then(({ activeProjectId: serverActive, lastRealProjectId: serverLastReal }) => {
        setActiveProjectIdState(serverActive);
        if (serverActive) localStorage.setItem(STORAGE_KEY, serverActive);
        else localStorage.removeItem(STORAGE_KEY);

        setLastRealProjectIdState(serverLastReal);
        if (serverLastReal) localStorage.setItem(LAST_REAL_STORAGE_KEY, serverLastReal);
        else localStorage.removeItem(LAST_REAL_STORAGE_KEY);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, cfg.msalClientId, cfg.apiBaseUrl]);

  const setActiveProjectId = useCallback((id: string | null) => {
    setActiveProjectIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);

    if (isRealProjectId(id)) {
      setLastRealProjectIdState(id);
      localStorage.setItem(LAST_REAL_STORAGE_KEY, id);
    }

    // Fire-and-forget: the global (extension-shared) selection lives on the backend, but a
    // network hiccup here must not roll back the optimistic local UI — the next mount's
    // reconciliation effect above will re-sync.
    createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl)
      .patchProjectSelection({ activeProjectId: id })
      .catch(() => {});
  }, [instance, cfg.msalClientId, cfg.apiBaseUrl]);

  // Used by the create-session 400/InvalidProject recovery flow: the stored "last real
  // project" id was rejected by the server as stale, so drop it rather than keep retrying.
  // Must also clear it on the backend, or the reconciliation effect above would silently
  // resurrect the just-cleared stale id on the very next load.
  const clearLastRealProjectId = useCallback(() => {
    setLastRealProjectIdState(null);
    localStorage.removeItem(LAST_REAL_STORAGE_KEY);

    createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl)
      .patchProjectSelection({ clearLastRealProjectId: true })
      .catch(() => {});
  }, [instance, cfg.msalClientId, cfg.apiBaseUrl]);

  // Drop a stale selection once the list has loaded (project archived/deleted elsewhere)
  // so the UI doesn't keep pointing at an id that no longer resolves — this clears back to
  // `null`, which RequireProject.tsx then treats as "select a project to continue".
  useEffect(() => {
    if (loading || !isRealProjectId(activeProjectId)) return;
    if (!projects.some(p => p.id === activeProjectId)) setActiveProjectId(null);
  }, [loading, projects, activeProjectId, setActiveProjectId]);

  // Same staleness check for the creation-tagging fallback — an archived project is
  // still a valid tag target (backend does an existence-only check), so only clear this
  // if the id no longer exists in either the active or archived list at all.
  useEffect(() => {
    if (loading || archivedLoading || !lastRealProjectId) return;
    const stillExists = projects.some(p => p.id === lastRealProjectId)
      || archivedProjects.some(p => p.id === lastRealProjectId);
    if (!stillExists) clearLastRealProjectId();
  }, [loading, archivedLoading, projects, archivedProjects, lastRealProjectId, clearLastRealProjectId]);

  const activeProject = useMemo(
    () => (isRealProjectId(activeProjectId) ? projects.find(p => p.id === activeProjectId) ?? null : null),
    [projects, activeProjectId],
  );

  const value = useMemo<ProjectCtx>(() => ({
    projects, archivedProjects, archivedLoading, activeProjectId, activeProject,
    lastRealProjectId, loading, setActiveProjectId, clearLastRealProjectId, refreshProjects,
  }), [projects, archivedProjects, archivedLoading, activeProjectId, activeProject,
      lastRealProjectId, loading, setActiveProjectId, clearLastRealProjectId, refreshProjects]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export default ProjectContext;
